import React, { createContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface ApplicationJobContext {
  jobTitle?: string;
  jobId?: string;
  jobSlug?: string;
  placementId?: string;
  placementSnapshotId?: string;
  publicationOperationId?: string;
}

export interface ApplicationContextType {
  isApplicationModalOpen: boolean;
  applicationData: ApplicationJobContext | null;
  currentJobContext: ApplicationJobContext | null;
  openApplicationModal: (jobData?: ApplicationJobContext) => void;
  closeApplicationModal: () => void;
  setJobContext: (jobData: ApplicationJobContext | null) => void;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

interface ApplicationProviderProps {
  children: ReactNode;
}

export const ApplicationProvider: React.FC<ApplicationProviderProps> = ({ children }) => {
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [applicationData, setApplicationData] = useState<ApplicationJobContext | null>(null);
  const [currentJobContext, setCurrentJobContext] = useState<ApplicationJobContext | null>(null);

  const openApplicationModal = useCallback((jobData?: ApplicationJobContext) => {
    // Use setter function to access current context value
    setCurrentJobContext((current) => {
      setApplicationData(jobData || current || null);
      return current; // Don't change the context, just use its value
    });
    setIsApplicationModalOpen(true);
  }, []);

  const closeApplicationModal = useCallback(() => {
    setIsApplicationModalOpen(false);
    setApplicationData(null);
  }, []);

  const setJobContext = useCallback((jobData: ApplicationJobContext | null) => {
    setCurrentJobContext(jobData);
  }, []);

  const value: ApplicationContextType = useMemo(() => ({
    isApplicationModalOpen,
    applicationData,
    currentJobContext,
    openApplicationModal,
    closeApplicationModal,
    setJobContext,
  }), [isApplicationModalOpen, applicationData, currentJobContext, openApplicationModal, closeApplicationModal, setJobContext]);

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
};

export { ApplicationContext };
